import React, { useRef, useState, useCallback } from "react";
import { Type, Palette, ImagePlus, RotateCcw, Minus, Plus, LetterText, Move } from "lucide-react";

const LOCAL_LOGO = "/doozygo-logo.png";

const FONT_OPTIONS = [
  { id: "dancing-script", name: "Dancing Script", family: "'Dancing Script', cursive" },
  { id: "playfair", name: "Playfair Display", family: "'Playfair Display', serif" },
  { id: "montserrat", name: "Montserrat", family: "'Montserrat', sans-serif" },
  { id: "great-vibes", name: "Great Vibes", family: "'Great Vibes', cursive" },
  { id: "oswald", name: "Oswald", family: "'Oswald', sans-serif" },
  { id: "lora", name: "Lora", family: "'Lora', serif" },
  { id: "pacifico", name: "Pacifico", family: "'Pacifico', cursive" },
  { id: "raleway", name: "Raleway", family: "'Raleway', sans-serif" },
  { id: "merriweather", name: "Merriweather", family: "'Merriweather', serif" },
  { id: "bebas-neue", name: "Bebas Neue", family: "'Bebas Neue', sans-serif" },
];

export const TemplateEditor = ({ template, customization, onChange, watermarkEnabled }) => {
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("text");

  const handlePhotoUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange({ ...customization, photo: ev.target.result });
    reader.readAsDataURL(file);
  }, [customization, onChange]);

  const handleReset = () => {
    onChange({
      title: template.defaultTitle,
      subtitle: template.defaultSubtitle,
      hostName: "", dateText: "", timeText: "",
      venueText: "", rsvpText: "", photo: null,
      titleColor: template.textColor,
      accentColor: template.accentColor,
      fontFamily: null, photoSize: 80,
      titleFontSize: 28, contentYOffset: 0,
    });
  };

  const tabs = [
    { id: "text", icon: Type, label: "Text" },
    { id: "font", icon: LetterText, label: "Font" },
    { id: "colors", icon: Palette, label: "Colors" },
    { id: "photo", icon: ImagePlus, label: "Photo" },
  ];

  return (
    <div data-testid="template-editor" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Preview */}
      <div className="order-1 lg:order-2">
        <div className="sticky top-24">
          <p className="text-xs text-center text-[#94a3b8] mb-2 flex items-center justify-center gap-1">
            <Move size={12} /> Drag text on preview to reposition
          </p>
          <InvitationPreview
            template={template}
            customization={customization}
            watermarkEnabled={watermarkEnabled}
            onDragOffset={(offset) => onChange({ ...customization, contentYOffset: offset })}
          />
        </div>
      </div>

      {/* Controls */}
      <div className="order-2 lg:order-1 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-[#1E1B4B]">Customize Your Invitation</h3>
          <button onClick={handleReset} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#E83E8C] transition-colors" data-testid="reset-editor-btn">
            <RotateCcw size={14} /> Reset
          </button>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 bg-[#F1F5F9] rounded-xl p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`editor-tab-${t.id}`}
              className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                activeTab === t.id ? "bg-white text-[#E83E8C] shadow-sm" : "text-[#475569] hover:text-[#1E1B4B]"
              }`}
            >
              <t.icon size={15} /> <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-[#E8E8E8] p-4 space-y-4">
          {activeTab === "text" && (
            <TextControls customization={customization} onChange={onChange} />
          )}
          {activeTab === "font" && (
            <FontControls customization={customization} onChange={onChange} />
          )}
          {activeTab === "colors" && (
            <ColorControls template={template} customization={customization} onChange={onChange} />
          )}
          {activeTab === "photo" && (
            <PhotoControls
              customization={customization}
              onChange={onChange}
              fileInputRef={fileInputRef}
              handlePhotoUpload={handlePhotoUpload}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const TextControls = ({ customization, onChange }) => {
  const update = (key, value) => onChange({ ...customization, [key]: value });
  const fields = [
    { key: "title", label: "Title", placeholder: "You're Invited!" },
    { key: "subtitle", label: "Subtitle", placeholder: "Join us for a celebration" },
    { key: "hostName", label: "Host Name", placeholder: "Hosted by..." },
    { key: "dateText", label: "Date", placeholder: "Saturday, March 15, 2026" },
    { key: "timeText", label: "Time", placeholder: "6:00 PM onwards" },
    { key: "venueText", label: "Venue", placeholder: "123 Park Avenue, Mumbai" },
    { key: "rsvpText", label: "RSVP / Contact", placeholder: "RSVP: your phone or email" },
  ];

  return (
    <div className="space-y-3">
      {/* Title font size control */}
      <div data-testid="title-size-control">
        <label className="block text-xs font-semibold text-[#475569] mb-1.5 uppercase tracking-wider">Title Size</label>
        <div className="flex items-center gap-3">
          <button onClick={() => update("titleFontSize", Math.max(16, (customization.titleFontSize || 28) - 2))}
            data-testid="title-size-decrease"
            className="w-8 h-8 rounded-lg bg-[#F1F5F9] flex items-center justify-center hover:bg-[#E83E8C]/10">
            <Minus size={14} className="text-[#475569]" />
          </button>
          <input type="range" min="16" max="48" value={customization.titleFontSize || 28}
            onChange={(e) => update("titleFontSize", parseInt(e.target.value))}
            data-testid="title-size-slider"
            className="flex-1 h-2 bg-[#E8E8E8] rounded-lg appearance-none cursor-pointer accent-[#E83E8C]" />
          <button onClick={() => update("titleFontSize", Math.min(48, (customization.titleFontSize || 28) + 2))}
            data-testid="title-size-increase"
            className="w-8 h-8 rounded-lg bg-[#F1F5F9] flex items-center justify-center hover:bg-[#E83E8C]/10">
            <Plus size={14} className="text-[#475569]" />
          </button>
          <span className="text-xs text-[#475569] w-10 text-right">{customization.titleFontSize || 28}px</span>
        </div>
      </div>

      {fields.map((f) => (
        <div key={f.key}>
          <label className="block text-xs font-semibold text-[#475569] mb-1 uppercase tracking-wider">{f.label}</label>
          {f.key === "venueText" ? (
            <textarea data-testid={`input-${f.key}`} value={customization[f.key] || ""}
              onChange={(e) => update(f.key, e.target.value)} placeholder={f.placeholder} rows={2}
              className="w-full px-3 py-2 border border-[#E8E8E8] rounded-lg text-sm focus:ring-2 focus:ring-[#E83E8C]/20 focus:border-[#E83E8C] outline-none resize-none" />
          ) : (
            <input data-testid={`input-${f.key}`} type="text" value={customization[f.key] || ""}
              onChange={(e) => update(f.key, e.target.value)} placeholder={f.placeholder}
              className="w-full px-3 py-2 border border-[#E8E8E8] rounded-lg text-sm focus:ring-2 focus:ring-[#E83E8C]/20 focus:border-[#E83E8C] outline-none" />
          )}
        </div>
      ))}

      {/* Vertical position control */}
      <div data-testid="position-control">
        <label className="block text-xs font-semibold text-[#475569] mb-1.5 uppercase tracking-wider">
          Text Position (or drag on preview)
        </label>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#475569]">Top</span>
          <input type="range" min="-80" max="80" value={customization.contentYOffset || 0}
            onChange={(e) => update("contentYOffset", parseInt(e.target.value))}
            data-testid="position-slider"
            className="flex-1 h-2 bg-[#E8E8E8] rounded-lg appearance-none cursor-pointer accent-[#E83E8C]" />
          <span className="text-xs text-[#475569]">Bottom</span>
        </div>
      </div>
    </div>
  );
};

const FontControls = ({ customization, onChange }) => {
  const selectedFont = customization.fontFamily || null;

  return (
    <div className="space-y-3" data-testid="font-controls">
      <p className="text-sm text-[#475569]">Choose a font style for your invitation</p>
      <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
        {FONT_OPTIONS.map((font) => {
          const isActive = selectedFont === font.family;
          return (
            <button key={font.id} data-testid={`font-${font.id}`}
              onClick={() => onChange({ ...customization, fontFamily: font.family })}
              className={`w-full flex items-center justify-between px-3 py-3 rounded-lg border-2 transition-all text-left ${
                isActive ? "border-[#E83E8C] bg-[#E83E8C]/5" : "border-[#E8E8E8] hover:border-[#E83E8C]/40"
              }`}>
              <div className="flex-1">
                <p className="text-xs text-[#475569] mb-0.5">{font.name}</p>
                <p style={{ fontFamily: font.family, fontSize: "1.1rem", color: "#1E1B4B" }}>
                  You're Invited!
                </p>
              </div>
              {isActive && (
                <div className="w-5 h-5 bg-[#E83E8C] rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ColorControls = ({ template, customization, onChange }) => {
  const update = (key, value) => onChange({ ...customization, [key]: value });
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wider">Text Color</label>
        <div className="flex items-center gap-3">
          <input data-testid="color-title" type="color" value={customization.titleColor || template.textColor}
            onChange={(e) => update("titleColor", e.target.value)}
            className="w-10 h-10 rounded-lg border-2 border-[#E8E8E8] cursor-pointer" />
          <span className="text-sm text-[#475569]">{customization.titleColor || template.textColor}</span>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wider">Accent Color</label>
        <div className="flex items-center gap-3">
          <input data-testid="color-accent" type="color" value={customization.accentColor || template.accentColor}
            onChange={(e) => update("accentColor", e.target.value)}
            className="w-10 h-10 rounded-lg border-2 border-[#E8E8E8] cursor-pointer" />
          <span className="text-sm text-[#475569]">{customization.accentColor || template.accentColor}</span>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wider">Quick Presets</label>
        <div className="flex gap-2 flex-wrap">
          {[
            { text: "#FFFFFF", accent: "#FFD700" },
            { text: "#FFD700", accent: "#E83E8C" },
            { text: "#1E1B4B", accent: "#E83E8C" },
            { text: "#FFFFFF", accent: "#FF6B6B" },
            { text: "#4A3728", accent: "#B8860B" },
            { text: "#2D5016", accent: "#E83E8C" },
          ].map((preset, pi) => (
            <button key={`preset-${preset.text}-${preset.accent}`}
              onClick={() => onChange({ ...customization, titleColor: preset.text, accentColor: preset.accent })}
              className="w-8 h-8 rounded-full border-2 border-[#E8E8E8] hover:scale-110 transition-transform"
              style={{ background: `linear-gradient(135deg, ${preset.text}, ${preset.accent})` }} />
          ))}
        </div>
      </div>
    </div>
  );
};

const PhotoControls = ({ customization, onChange, fileInputRef, handlePhotoUpload }) => {
  const photoSize = customization.photoSize || 80;
  const adjustSize = (delta) => onChange({ ...customization, photoSize: Math.max(40, Math.min(160, photoSize + delta)) });

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#475569]">Upload a photo to add to your invitation</p>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
      <button onClick={() => fileInputRef.current?.click()} data-testid="upload-photo-btn"
        className="w-full py-3 border-2 border-dashed border-[#E83E8C]/30 rounded-xl text-[#E83E8C] font-medium hover:bg-[#E83E8C]/5 transition-colors flex items-center justify-center gap-2">
        <ImagePlus size={20} /> {customization.photo ? "Change Photo" : "Upload Photo"}
      </button>
      {customization.photo && (
        <>
          <div className="relative">
            <img src={customization.photo} alt="Uploaded" className="w-full h-32 object-cover rounded-lg" />
            <button onClick={() => onChange({ ...customization, photo: null, photoSize: 80 })}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">x</button>
          </div>
          <div data-testid="photo-size-control">
            <label className="block text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wider">Photo Size</label>
            <div className="flex items-center gap-3">
              <button onClick={() => adjustSize(-10)} data-testid="photo-size-decrease"
                className="w-8 h-8 rounded-lg bg-[#F1F5F9] flex items-center justify-center hover:bg-[#E83E8C]/10"><Minus size={16} className="text-[#475569]" /></button>
              <input type="range" min="40" max="160" value={photoSize}
                onChange={(e) => onChange({ ...customization, photoSize: parseInt(e.target.value) })}
                data-testid="photo-size-slider"
                className="flex-1 h-2 bg-[#E8E8E8] rounded-lg appearance-none cursor-pointer accent-[#E83E8C]" />
              <button onClick={() => adjustSize(10)} data-testid="photo-size-increase"
                className="w-8 h-8 rounded-lg bg-[#F1F5F9] flex items-center justify-center hover:bg-[#E83E8C]/10"><Plus size={16} className="text-[#475569]" /></button>
              <span className="text-xs text-[#475569] w-10 text-right">{photoSize}px</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const InvitationPreview = ({ template, customization, watermarkEnabled, previewRef, onDragOffset }) => {
  const titleColor = customization.titleColor || template.textColor;
  const accentColor = customization.accentColor || template.accentColor;
  const occasionLabel = customization.customOccasionLabel || template.occasionLabel;
  const fontFamily = customization.fontFamily || "'Playfair Display', serif";
  const photoSize = customization.photoSize || 80;
  const titleFontSize = customization.titleFontSize || 28;
  const contentYOffset = customization.contentYOffset || 0;

  const needsShadow = isLightColor(titleColor);
  const textShadow = needsShadow ? "none" : "0 1px 4px rgba(0,0,0,0.5)";

  // Drag handling
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startOffset = useRef(0);

  const handlePointerDown = (e) => {
    if (!onDragOffset) return;
    isDragging.current = true;
    startY.current = e.clientY || e.touches?.[0]?.clientY || 0;
    startOffset.current = contentYOffset;
    e.currentTarget.style.cursor = "grabbing";
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current || !onDragOffset) return;
    const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
    const containerHeight = dragRef.current?.parentElement?.offsetHeight || 400;
    const deltaPixels = currentY - startY.current;
    const deltaPercent = (deltaPixels / containerHeight) * 160;
    const newOffset = Math.max(-80, Math.min(80, startOffset.current + deltaPercent));
    onDragOffset(Math.round(newOffset));
  };

  const handlePointerUp = (e) => {
    isDragging.current = false;
    if (e.currentTarget) e.currentTarget.style.cursor = "grab";
  };

  return (
    <div
      ref={previewRef}
      data-testid="invitation-preview"
      className="relative mx-auto shadow-2xl rounded-2xl overflow-hidden select-none"
      style={{
        width: "100%", maxWidth: "420px", aspectRatio: "3/4",
        WebkitUserSelect: "none", userSelect: "none",
        WebkitTouchCallout: "none", pointerEvents: "auto",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Background image */}
      <img src={template.image} alt="" className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        style={{ pointerEvents: "none" }}
      />

      {/* Overlay */}
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.15)" }} />

      {/* Draggable content */}
      <div
        ref={dragRef}
        className="relative z-10 flex flex-col items-center justify-center h-full px-5 py-4 text-center"
        style={{
          fontFamily,
          transform: `translateY(${contentYOffset}px)`,
          cursor: onDragOffset ? "grab" : "default",
          touchAction: "none",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {/* Occasion label */}
        <p className="uppercase tracking-[3px] font-bold mb-2" style={{
          color: accentColor, fontSize: "0.65rem",
          textShadow: "0 1px 3px rgba(0,0,0,0.5)",
        }}>
          {occasionLabel}
        </p>

        {/* Title */}
        <h2 className="font-bold leading-tight mb-1" style={{
          color: titleColor, fontSize: `${titleFontSize}px`, lineHeight: 1.15, textShadow,
        }}>
          {customization.title || template.defaultTitle}
        </h2>

        {/* Subtitle */}
        <p className="mb-3 font-medium" style={{
          color: titleColor, fontSize: `${Math.max(11, titleFontSize * 0.45)}px`,
          opacity: 0.9, textShadow,
        }}>
          {customization.subtitle || template.defaultSubtitle}
        </p>

        {/* Divider */}
        <div className="w-12 h-0.5 rounded-full mb-3" style={{ backgroundColor: accentColor }} />

        {/* Photo */}
        {customization.photo && (
          <div className="rounded-full overflow-hidden mb-3 mx-auto shadow-lg flex-shrink-0" style={{
            width: `${photoSize}px`, height: `${photoSize}px`, border: `3px solid ${accentColor}`,
          }}>
            <img src={customization.photo} alt="" className="w-full h-full object-cover" draggable={false} />
          </div>
        )}

        {/* Host name */}
        {customization.hostName && (
          <p className="font-semibold mb-2" style={{
            color: titleColor, fontSize: `${Math.max(11, titleFontSize * 0.45)}px`, textShadow,
          }}>
            {customization.hostName}
          </p>
        )}

        {/* Event details */}
        <div className="space-y-0.5 mb-2">
          {customization.dateText && (
            <p className="font-bold" style={{
              color: titleColor, fontSize: `${Math.max(11, titleFontSize * 0.45)}px`, textShadow,
            }}>
              {customization.dateText}
            </p>
          )}
          {customization.timeText && (
            <p className="font-medium" style={{
              color: titleColor, fontSize: `${Math.max(10, titleFontSize * 0.4)}px`,
              opacity: 0.9, textShadow,
            }}>
              {customization.timeText}
            </p>
          )}
          {customization.venueText && (
            <p className="font-medium mt-1" style={{
              color: titleColor, fontSize: `${Math.max(9, titleFontSize * 0.35)}px`,
              opacity: 0.85, textShadow, maxWidth: "280px",
            }}>
              {customization.venueText}
            </p>
          )}
        </div>
      </div>

      {/* RSVP — absolutely positioned at bottom, won't shift content */}
      {customization.rsvpText && (
        <div className="absolute bottom-10 left-0 right-0 z-10 flex justify-center" style={{ fontFamily }}>
          <p className="font-bold uppercase tracking-wider px-3 py-1 rounded-full inline-block" style={{
            color: accentColor, fontSize: `${Math.max(9, titleFontSize * 0.35)}px`,
            backgroundColor: "rgba(0,0,0,0.25)", backdropFilter: "blur(4px)",
            border: `1px solid ${accentColor}60`, textShadow: "0 1px 3px rgba(0,0,0,0.5)",
          }}>
            {customization.rsvpText}
          </p>
        </div>
      )}

      {/* DoozyGo Watermark - diagonal faded overlay + bottom bar */}
      {watermarkEnabled && (
        <>
          {/* Diagonal repeated watermark across the entire image - 20% opacity */}
          <div
            data-testid="doozygo-watermark-overlay"
            className="absolute inset-0 z-15 pointer-events-none overflow-hidden"
            style={{ opacity: 0.2 }}
          >
            <div className="absolute inset-0" style={{
              transform: "rotate(-30deg) scale(1.5)",
              transformOrigin: "center center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "40px",
            }}>
              {[...Array(6)].map((_, rowIdx) => (
                <div key={`wm-r-${rowIdx}`} className="flex items-center gap-8 whitespace-nowrap">
                  {[...Array(4)].map((_, colIdx) => (
                    <span key={`wm-c-${colIdx}`} className="text-white font-bold text-lg tracking-widest"
                      style={{ textShadow: "0 1px 3px rgba(0,0,0,0.8)" }}>
                      DOOZYGO
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {/* Bottom bar watermark */}
          <div data-testid="doozygo-watermark"
            className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-2.5 z-20"
            style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}>
            <img src={LOCAL_LOGO} alt="DoozyGo" className="h-6 mr-2" style={{ filter: "brightness(10)" }} />
            <span className="text-white text-xs font-semibold opacity-90">Made with DoozyGo</span>
          </div>
        </>
      )}
    </div>
  );
};

function isLightColor(hex) {
  if (!hex) return false;
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}
